import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Swords, Crown, Users, Clock, Play, Flame } from "lucide-react";
import { api } from "../lib/api";
import { Page, Loader } from "../components/UI";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function Flag({ iso }) {
  return iso ? <span className="text-[10px] font-mono text-slate-400 ml-1">{iso}</span> : null;
}

function CouponCard({ side, coupon, player, flipped, leader }) {
  const isLeader = leader?.side === side;
  return (
    <div data-testid={`duel-coupon-${side.toLowerCase()}`}
      className={`flex-1 rounded-2xl border p-5 min-h-[260px] [perspective:1000px] ${isLeader ? "border-[#E5B842]/60" : "border-[#2b2512]"} bg-[#070707]`}>
      <div className="flex items-center justify-between mb-3">
        <div className="font-heading font-bold text-slate-100">{player?.display_name}<Flag iso={player?.country_iso3} /></div>
        {player?.rating && <div className="text-xs font-mono text-slate-500">{player.rating}</div>}
      </div>
      <div className="relative" style={{ transition: "transform 0.6s", transformStyle: "preserve-3d", transform: flipped ? "rotateY(0deg)" : "rotateY(180deg)" }}>
        {coupon?.hidden ? (
          <div className="rounded-xl bg-gradient-to-br from-[#161616] to-[#070707] border border-[#2b2512] h-40 flex items-center justify-center">
            <Swords className="w-8 h-8 text-slate-600" />
            <span className="ml-2 text-xs font-mono text-slate-500 uppercase tracking-widest">Blind · {coupon?.selection_count} picks</span>
          </div>
        ) : (
          <div className="space-y-1.5">
            {(coupon?.selections || []).map((s, i) => (
              <div key={i} className="flex items-center justify-between text-xs rounded-lg bg-[#161616] px-3 py-2">
                <span className="text-slate-300 truncate max-w-[60%]">{s.label || s.event_name}</span>
                <span className="flex items-center gap-2">
                  <span className="font-mono text-slate-400">{Number(s.odds).toFixed(2)}</span>
                  <span className={`text-[10px] font-bold font-mono px-1.5 py-0.5 rounded ${s.status === "WON" ? "bg-emerald-950 text-emerald-300" : s.status === "LOST" ? "bg-rose-950 text-rose-300" : "bg-slate-800 text-slate-500"}`}>{s.status}</span>
                </span>
              </div>
            ))}
            {coupon?.total_odds && <div className="text-right text-xs font-mono text-[#E5B842] pt-1">Total {Number(coupon.total_odds).toFixed(2)}</div>}
          </div>
        )}
      </div>
    </div>
  );
}

export default function DuelTheatrePage() {
  const { id } = useParams();
  const [duel, setDuel] = useState(null);
  const [spectators, setSpectators] = useState(0);
  const [countdown, setCountdown] = useState(0);
  const [replay, setReplay] = useState(null);
  const esRef = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/theatre/duel/${id}`);
    setDuel(data);
    setSpectators(data.spectators || 0);
    setCountdown(data.countdown_seconds || 0);
    return data;
  }, [id]);

  useEffect(() => {
    load();
    const es = new EventSource(`${BACKEND}/api/theatre/duel/${id}/stream`);
    es.onmessage = (e) => {
      try {
        const ev = JSON.parse(e.data);
        if (ev.type === "duel.spectators") setSpectators(ev.payload.count);
        else load(); // reveal / selection / resolved -> refetch (server-driven)
      } catch (_) {}
    };
    esRef.current = es;
    return () => es.close();
  }, [id, load]);

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setInterval(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [countdown]);

  // replay playback (chronological timeline)
  useEffect(() => {
    if (!replay || !duel?.timeline?.length) return;
    let i = 0;
    const steps = duel.timeline;
    const t = setInterval(() => {
      i += 1;
      setReplay({ idx: i });
      if (i >= steps.length) clearInterval(t);
    }, 1400);
    return () => clearInterval(t);
  }, [replay?.on]); // eslint-disable-line

  if (!duel) return <Loader label="Loading duel theatre…" />;

  const revealed = duel.revealed;
  const resolved = duel.status === "RESOLVED";
  const winnerSide = duel.winner_player_id === duel.home?.id ? "HOME" : duel.winner_player_id === duel.away?.id ? "AWAY" : null;

  return (
    <Page>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#E5B842] font-mono flex items-center gap-2">
            {!duel.rated && <span className="px-2 py-0.5 rounded-full bg-fuchsia-950/60 text-fuchsia-300 border border-fuchsia-500/40" data-testid="duel-showmatch-badge">SHOWMATCH · NON-RATED</span>}
            {duel.rated && <span>{duel.competition_context}</span>}
          </div>
          <h1 className="font-heading text-3xl font-black text-slate-50 mt-1">{duel.label}</h1>
          <div className="text-xs text-slate-500">{duel.competition_context}</div>
        </div>
        <div className="text-right">
          <div data-testid="duel-spectators" className="inline-flex items-center gap-1.5 text-sm text-slate-300"><Users className="w-4 h-4 text-[#F0C72A]" />{spectators} watching</div>
        </div>
      </div>

      {duel.head_to_head && duel.head_to_head.played > 0 && (
        <div className="text-xs font-mono text-slate-500 mb-4">Head-to-head: {duel.head_to_head.a_wins}–{duel.head_to_head.b_wins}{duel.head_to_head.draws ? `–${duel.head_to_head.draws}` : ""} ({duel.head_to_head.played} played)</div>
      )}

      {!revealed && (
        <div data-testid="duel-countdown" className="rounded-2xl border border-[#2b2512] bg-[#070707] p-8 text-center mb-6">
          <Clock className="w-8 h-8 text-[#E5B842] mx-auto mb-2" />
          <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-mono">Reveal in</div>
          <div className="font-heading text-6xl font-black text-slate-50 tabular-nums">{countdown}s</div>
          <div className="text-xs text-slate-500 mt-2">Coupons are sealed — blind until both lock simultaneously.</div>
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-4 items-stretch mb-6">
        <CouponCard side="HOME" coupon={duel.home_coupon} player={duel.home} flipped={revealed} leader={duel.leader} />
        <div className="flex items-center justify-center md:flex-col gap-2 px-2">
          <Swords className="w-7 h-7 text-slate-500" />
          <span className="text-[10px] font-mono text-slate-600">VS</span>
        </div>
        <CouponCard side="AWAY" coupon={duel.away_coupon} player={duel.away} flipped={revealed} leader={duel.leader} />
      </div>

      {revealed && !resolved && duel.leader && (
        <div data-testid="duel-leader" className="rounded-xl border border-[#2b2512] bg-[#070707] px-4 py-3 mb-6 text-sm flex items-center gap-2">
          <Flame className="w-4 h-4 text-[#E5B842]" />
          {duel.leader.side ? (
            <span><span className="text-slate-100 font-bold">{duel.leader.side === "HOME" ? duel.home.display_name : duel.away.display_name}</span> <span className="text-slate-400">leads — {duel.leader.reason}</span></span>
          ) : <span className="text-slate-400">{duel.leader.reason}</span>}
          <span className="ml-auto text-xs font-mono text-slate-500">{duel.settled}/{duel.total_selections} settled</span>
        </div>
      )}

      {resolved && (
        <div data-testid="duel-resolution" className="rounded-2xl foil-border bg-gradient-to-r from-[#2a2410] to-[#070707] p-6 mb-6 flex items-center gap-4">
          <Crown className="w-10 h-10 text-[#E5B842]" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#E5B842] font-mono">Winner</div>
            <div className="font-heading text-2xl font-black text-slate-50">
              {winnerSide === "HOME" ? duel.home.display_name : winnerSide === "AWAY" ? duel.away.display_name : "Draw"}
            </div>
            <div className="text-sm text-slate-400">{duel.deciding_criterion}</div>
          </div>
          {duel.is_replay && (
            <button data-testid="duel-replay-btn" onClick={() => setReplay({ on: Date.now(), idx: 0 })}
              className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#E5B842] text-[#000000] text-sm font-bold">
              <Play className="w-4 h-4" /> Watch replay
            </button>
          )}
        </div>
      )}

      {(duel.timeline?.length > 0) && (
        <div data-testid="duel-timeline">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono mb-2">Timeline{replay ? " · replaying" : ""}</div>
          <div className="space-y-1.5">
            {duel.timeline.map((t, i) => (
              <div key={i} className={`flex items-center gap-3 text-xs rounded-lg px-3 py-2 ${replay && i >= (replay.idx ?? 0) ? "opacity-30" : "opacity-100"} bg-[#070707] border border-[#161616]`}
                style={{ transition: "opacity 0.4s ease" }}>
                <span className="font-mono text-[#E5B842] uppercase w-24 shrink-0">{t.kind}</span>
                <span className="text-slate-300">{t.text}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6"><Link to="/live" className="text-xs text-slate-500 hover:text-[#E5B842]">← Back to Live</Link></div>
    </Page>
  );
}
